import { FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FormLayout, Label } from '@/components/Forms';
import CurrencyField from '@/components/Forms/CurrencyField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import useAlerts from '@/hooks/useAlerts';
import { selectTeamTestingProperties } from '@/selectors';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';
import { Dispatch, RootState } from '@/utils/store';

import PasteFileNameField from './PasteFileNameField';
import TestingChannelsSelectionField from './TestingChannelsSelectionField';
import ToggableCardHeading, { Box } from './ToggableCardHeading';

type FormDataT = {
  sequences_max_cpv: string;
  sequences_youtube_channels?: number[];
  sequences_campaign_budget: string;
  sequences_paste_file_name: boolean;
};

const InputsBox = styled.div`
  padding: 24px;
  border-top: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

const YoutubeSequencesProperties = () => {
  const dispatch = useDispatch<Dispatch>();

  const [showContent, setShowContent] = useState(false);

  const { teamId } = useParams();

  const alerts = useAlerts();

  const { testingSettings, testingChannels } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!),
  );

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      sequences_max_cpv: testingSettings?.sequences_max_cpv?.toString() ?? '',
      sequences_campaign_budget:
        testingSettings?.sequences_campaign_budget?.toString() ?? '',
      sequences_youtube_channels:
        testingChannels
          ?.filter((c) => c.type === 'sequences')
          ?.map((c) => c.id) ?? [],
      sequences_paste_file_name:
        testingSettings?.sequences_paste_file_name ?? false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const properties = await api.post(
          `/v1/teams/${teamId}/testing-settings`,
          {
            ...data,
            network: 'youtube',
            type: 'sequences',
          },
        );

        dispatch.teams.updateTeamTestingProperties({
          properties,
          teamId: parseInt(teamId!),
        });

        alerts.success('Youtube Sequences properties and channels saved');
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Box>
      <form onSubmit={onSubmit}>
        <ToggableCardHeading
          setShowContent={setShowContent}
          count={
            testingChannels
              ?.filter((c) => c.type === 'sequences')
              ?.map((c) => c.id).length
          }
          showContent={showContent}
          title="Sequences / Intros Properties & channels"
          hasSubmitButton
          isSubmitting={isSubmitting}
          isDirty={isDirty}
        />
        <InputsBox>
          <FormLayout>
            <PasteFileNameField<FormDataT>
              control={control}
              name="sequences_paste_file_name"
            />
            <div>
              <Label htmlFor="sequencesMaxCpv">Sequences Maximum CPV</Label>
              <CurrencyField<FormDataT>
                control={control}
                name="sequences_max_cpv"
                inputId="sequencesMaxCpv"
                placeholder="Add amount"
              />
              {!!errors.sequences_max_cpv && (
                <ValidationMessage>
                  {errors.sequences_max_cpv.message}
                </ValidationMessage>
              )}
            </div>
            <div>
              <Label htmlFor="sequencesBudget">Sequences Campaign Budget</Label>
              <CurrencyField<FormDataT>
                control={control}
                name="sequences_campaign_budget"
                inputId="sequencesBudget"
                placeholder="Add amount"
              />
              {!!errors.sequences_campaign_budget && (
                <ValidationMessage>
                  {errors.sequences_campaign_budget.message}
                </ValidationMessage>
              )}
            </div>
          </FormLayout>
        </InputsBox>
        {!!showContent && (
          <TestingChannelsSelectionField<FormDataT>
            control={control}
            name="sequences_youtube_channels"
            label="Channels Options"
          />
        )}
      </form>
    </Box>
  );
};

export default YoutubeSequencesProperties;
